import type { GridInitialState } from '@mui/x-data-grid-pro';
import { DateTime } from 'luxon';
import type { IntlShape } from 'react-intl';
import { ReportFieldsContent, ReportType } from 'shared/domain/report/types/model/common';
import { LocaleCountryAndLanguageCode } from 'shared/types/locale';
import { Field } from 'shared/domain/visibleField/availableFields';
import { HashMap, LabelledEntity } from 'shared/types/commonView';
import {
  getReportAdditionalContentExpanded,
  getLastReportLocale,
  getSavedReportConfig,
  getSavedReportFieldsContent,
  getSavedSelectedFields,
  migrateLegacyReportLocalStorage,
  saveFieldsContent,
  saveLastReportLocale,
  saveReportAdditionalContentExpanded,
  saveReportConfig,
  saveSelectedFields,
  type SavedReportConfig,
} from './pdfReportPreferencesStorage';
import {
  getSavedInspectionReportContentConfig,
  saveInspectionReportContentConfig,
  type SavedInspectionReportContentConfig,
} from './inspectionReportContentStorage';
import { LocalStorage } from './LocalStorage';
import { LocalStorageKeyFactory, DataGridInstanceName, type LocalStorageKey } from './keys';
import { VersionedGridState } from 'components/common/dataGrid/columnsFactory/types';
import {
  applyMigrations,
  getTargetVersion,
  hasMigrations,
} from './migrations/dataGridMigrations';
import { isPlainObject } from 'shared/utils/object';

/**
 * Business logic layer for localStorage operations.
 * 
 * This class encapsulates all domain-specific storage logic:
 * - Knows about storage keys (via LocalStorageKeyFactory)
 * - Knows about data types and validation
 * - Provides semantic, domain-focused API
 * 
 * Components and hooks should use StorageManager, not LocalStorage directly.
 * LocalStorage is a pure DAO layer (get/set/delete with JSON parsing).
 * 
 * @example
 * // Component doesn't know about localStorage at all:
 * if (StorageManager.shouldShowInstallAppSnackbar()) {
 *   // show snackbar
 * }
 * StorageManager.markInstallAppSnackbarClosed();
 */
export class LocalStorageManager {
  // ===========================================================================
  // User Data (for logging - silent mode to prevent infinite recursion)
  // ===========================================================================

  /**
   * Gets user email silently (without error logging).
   * Use only in logging utilities to prevent infinite recursion.
   */
  static getUserEmailSilent(): string {
    return LocalStorage.getSilent(LocalStorageKeyFactory.USER_EMAIL, '');
  }

  /**
   * Gets user ID silently (without error logging).
   * Use only in logging utilities to prevent infinite recursion.
   */
  static getUserIdSilent(): string {
    return LocalStorage.getSilent(LocalStorageKeyFactory.USER_ID, '');
  }

  /**
   * Gets user email (with error logging).
   * Use for normal operations. For logging utilities, use getUserEmailSilent().
   */
  static getUserEmail(): string {
    return LocalStorage.get<string>(LocalStorageKeyFactory.USER_EMAIL, '');
  }

  /**
   * Gets user ID (with error logging).
   * Use for normal operations. For logging utilities, use getUserIdSilent().
   */
  static getUserId(): string {
    return LocalStorage.get<string>(LocalStorageKeyFactory.USER_ID, '');
  }

  /**
   * Gets user email with fallback to default value.
   * Useful for logging/monitoring where 'unknown' is preferred over empty string.
   */
  static getUserEmailOrDefault(defaultValue = 'unknown'): string {
    const email = this.getUserEmail();
    return email || defaultValue;
  }

  /**
   * Gets user ID with fallback to default value.
   * Useful for logging/monitoring where 'unknown' is preferred over empty string.
   */
  static getUserIdOrDefault(defaultValue = 'unknown'): string {
    const userId = this.getUserId();
    return userId || defaultValue;
  }

  /**
   * Sets user data (email and ID) in localStorage.
   */
  static setUserData(email: string, userId: string): void {
    LocalStorage.set(LocalStorageKeyFactory.USER_EMAIL, email);
    LocalStorage.set(LocalStorageKeyFactory.USER_ID, userId);
  }

  // ===========================================================================
  // Authentication
  // ===========================================================================

  /**
   * Gets authentication token.
   */
  static getAuthToken(): string {
    return LocalStorage.get<string>(LocalStorageKeyFactory.AUTH_TOKEN, '');
  }

  /**
   * Gets authentication token expiration timestamp.
   */
  static getAuthTokenExpiration(): string {
    return LocalStorage.get<string>(LocalStorageKeyFactory.AUTH_TOKEN_EXPIRATION, '');
  }

  /**
   * Sets authentication token.
   */
  static setAuthToken(token: string): void {
    LocalStorage.set(LocalStorageKeyFactory.AUTH_TOKEN, token);
  }

  /**
   * Sets authentication token expiration timestamp.
   */
  static setAuthTokenExpiration(expiration: string): void {
    LocalStorage.set(LocalStorageKeyFactory.AUTH_TOKEN_EXPIRATION, expiration);
  }

  // ===========================================================================
  // Workflow Stepper
  // ===========================================================================

  /**
   * Gets workflow stepper expansion state.
   * Returns true (expanded) by default.
   */
  static getWorkflowStepperExpanded(): boolean {
    return LocalStorage.get<boolean>(LocalStorageKeyFactory.WORKFLOW_STEPPER_EXPANDED, true);
  }

  /**
   * Sets workflow stepper expansion state.
   */
  static setWorkflowStepperExpanded(expanded: boolean): void {
    LocalStorage.set(LocalStorageKeyFactory.WORKFLOW_STEPPER_EXPANDED, expanded);
  }

  // ===========================================================================
  // Rich text colours
  // ===========================================================================

  private static getRecentTextColorsKey(): LocalStorageKey {
    const userId = LocalStorage.get<string>(LocalStorageKeyFactory.USER_ID, '');
    return LocalStorageKeyFactory.createUserScopedKey(userId, LocalStorageKeyFactory.RICH_TEXT_RECENT_COLORS);
  }

  /** The text colours the user picked last, newest first (`#RRGGBB`). */
  static getRecentTextColors(): string[] {
    const stored = LocalStorage.get<unknown>(this.getRecentTextColorsKey(), []);
    return Array.isArray(stored) ? stored.filter((color): color is string => typeof color === 'string') : [];
  }

  static setRecentTextColors(colors: string[]): void {
    LocalStorage.set(this.getRecentTextColorsKey(), colors);
  }

  // ===========================================================================
  // Analytics section filters
  // ===========================================================================

  private static getAnalyticsSectionFiltersKey(projectId: string, section: string): LocalStorageKey {
    const userId = LocalStorage.get<string>(LocalStorageKeyFactory.USER_ID, '');
    return LocalStorageKeyFactory.createUserScopedKey(
      userId,
      LocalStorageKeyFactory.createAnalyticsSectionFiltersKey(projectId, section)
    );
  }

  /** Values chosen in the filters of a dashboard section, by dimension. */
  static getAnalyticsSectionFilters(projectId: string, section: string): Record<string, unknown> {
    const stored = LocalStorage.get<unknown>(this.getAnalyticsSectionFiltersKey(projectId, section), {});
    return isPlainObject(stored) ? stored : {};
  }

  static setAnalyticsSectionFilters(projectId: string, section: string, values: Record<string, unknown>): void {
    LocalStorage.set(this.getAnalyticsSectionFiltersKey(projectId, section), values);
  }

  // ===========================================================================
  // Install App Snackbar
  // ===========================================================================

  /**
   * Clears authentication token and expiration.
   */
  static clearAuthToken(): void {
    LocalStorage.delete(LocalStorageKeyFactory.AUTH_TOKEN);
    LocalStorage.delete(LocalStorageKeyFactory.AUTH_TOKEN_EXPIRATION);
  }

  // ===========================================================================
  // Issue Filters
  // ===========================================================================

  /**
   * Gets saved issue filters for a given unique ID.
   */
  static getIssueFilters<T>(uniqueId: string, defaultValue: T): T {
    const key = LocalStorageKeyFactory.createIssueFiltersKey(uniqueId);
    return LocalStorage.get<T>(key, defaultValue);
  }

  /**
   * Saves issue filters for a given unique ID.
   */
  static saveIssueFilters<T>(uniqueId: string, filters: T): void {
    const key = LocalStorageKeyFactory.createIssueFiltersKey(uniqueId);
    LocalStorage.set(key, filters);
  }

  // ===========================================================================
  // Issue Parameters
  // ===========================================================================

  /**
   * Gets saved issue parameters for a given project ID.
   */
  static getIssueParameters<T>(projectId: string, defaultValue: T): T {
    const key = LocalStorageKeyFactory.createIssueParametersKey(projectId);
    return LocalStorage.get<T>(key, defaultValue);
  }

  /**
   * Saves issue parameters for a given project ID.
   */
  static saveIssueParameters<T>(projectId: string, parameters: T): void {
    const key = LocalStorageKeyFactory.createIssueParametersKey(projectId);
    LocalStorage.set(key, parameters);
  }

  // ===========================================================================
  // Install Mobile App Snackbar
  // ===========================================================================

  private static readonly SNACKBAR_COOLDOWN_DAYS = 7;

  static shouldShowInstallAppSnackbar(): boolean {
    const userClosedDate = LocalStorage.get<string>(
      LocalStorageKeyFactory.CLOSED_INSTALL_MOBILE_APP_SNACKBAR_DATE,
      ''
    );
    if (!userClosedDate) {
      return true;
    }
    const parsedDate = DateTime.fromISO(userClosedDate);
    const diff = parsedDate.diffNow('days').days;
    return Math.abs(diff) > this.SNACKBAR_COOLDOWN_DAYS;
  }

  static markInstallAppSnackbarClosed(): void {
    LocalStorage.set(
      LocalStorageKeyFactory.CLOSED_INSTALL_MOBILE_APP_SNACKBAR_DATE,
      DateTime.now().toISO()
    );
  }

  // ===========================================================================
  // Autocomplete
  // ===========================================================================

  static getAutocompleteValues(baseKey: string): string[] {
    const storageKey = LocalStorageKeyFactory.createAutocompleteKey(baseKey);
    const item = LocalStorage.get<unknown>(storageKey, []);
    return Array.isArray(item) && item.every((i) => typeof i === 'string')
      ? (item as string[])
      : [];
  }

  static setAutocompleteValues(baseKey: string, values: string[]): void {
    const storageKey = LocalStorageKeyFactory.createAutocompleteKey(baseKey);
    LocalStorage.set(storageKey, values);
  }

  static addAutocompleteValue(baseKey: string, value: string, limit = 50): string[] {
    const items = this.getAutocompleteValues(baseKey);
    const index = items.indexOf(value);

    if (index === 0) return items;
    if (index > 0) items.splice(index, 1);

    items.unshift(value);
    if (items.length > limit) items.length = limit;

    this.setAutocompleteValues(baseKey, items);
    return items;
  }

  static removeAutocompleteValue(baseKey: string, value: string): string[] {
    const items = this.getAutocompleteValues(baseKey);
    const index = items.indexOf(value);
    if (index !== -1) {
      items.splice(index, 1);
      this.setAutocompleteValues(baseKey, items);
    }
    return items;
  }

  // ===========================================================================
  // DataGrid
  // ===========================================================================

  private static getDataGridStateKey(instanceName: DataGridInstanceName) {
    const userId =
      typeof window !== 'undefined'
        ? LocalStorage.get<string>(LocalStorageKeyFactory.USER_ID, '')
        : '';

    const baseKey = LocalStorageKeyFactory.createDataGridKey(instanceName);
    return LocalStorageKeyFactory.createUserScopedKey(userId, baseKey);
  }

  private static getDataGridFilterPresetsKey(instanceName: DataGridInstanceName) {
    const userId =
      typeof window !== 'undefined'
        ? LocalStorage.get<string>(LocalStorageKeyFactory.USER_ID, '')
        : '';

    const baseKey =
      LocalStorageKeyFactory.createDataGridFilterPresetsKey(instanceName);
    return LocalStorageKeyFactory.createUserScopedKey(userId, baseKey);
  }

  static getDataGridState(instanceName: DataGridInstanceName): GridInitialState | null {
    const key = this.getDataGridStateKey(instanceName);
    const stored = LocalStorage.get<GridInitialState | null>(key, null);

    if (!stored || !hasMigrations(instanceName)) {
      return stored;
    }

    const incoming = stored as VersionedGridState;
    const currentVersion = incoming._version ?? 0;
    const targetVersion = getTargetVersion(instanceName);

    if (currentVersion >= targetVersion) {
      return stored;
    }

    const migrated = applyMigrations(instanceName, incoming);
    const versioned: VersionedGridState = { ...migrated, _version: targetVersion };
    // Persist the migrated state so subsequent loads (and grid auto-saves
    // that read _version back) see the canonical layout immediately.
    LocalStorage.set(key, versioned);
    return versioned as GridInitialState;
  }

  static setDataGridState(
    instanceName: DataGridInstanceName,
    state: GridInitialState
  ): void {
    const key = this.getDataGridStateKey(instanceName);

    if (!hasMigrations(instanceName)) {
      LocalStorage.set(key, state);
      return;
    }

    const incoming = state as VersionedGridState;
    const currentVersion = incoming._version ?? 0;
    const targetVersion = getTargetVersion(instanceName);

    const migrated = currentVersion < targetVersion
      ? applyMigrations(instanceName, incoming)
      : incoming;

    const toSave: VersionedGridState = { ...migrated, _version: targetVersion };
    LocalStorage.set(key, toSave);
  }

  static getDataGridFilterPresets<T>(
    instanceName: DataGridInstanceName,
    defaultValue: T
  ): T {
    const key = this.getDataGridFilterPresetsKey(instanceName);
    return LocalStorage.get<T>(key, defaultValue);
  }

  static setDataGridFilterPresets<T>(
    instanceName: DataGridInstanceName,
    presets: T
  ): void {
    const key = this.getDataGridFilterPresetsKey(instanceName);
    LocalStorage.set(key, presets);
  }

  static getDataGridSelectedPresetId(
    instanceName: DataGridInstanceName
  ): string {
    const userId =
      typeof window !== 'undefined'
        ? LocalStorage.get<string>(LocalStorageKeyFactory.USER_ID, '')
        : '';

    const baseKey =
      LocalStorageKeyFactory.createDataGridSelectedPresetKey(instanceName);
    const key = LocalStorageKeyFactory.createUserScopedKey(userId, baseKey);
    return LocalStorage.get<string>(key, '');
  }

  static setDataGridSelectedPresetId(
    instanceName: DataGridInstanceName,
    presetId: string
  ): void {
    const userId =
      typeof window !== 'undefined'
        ? LocalStorage.get<string>(LocalStorageKeyFactory.USER_ID, '')
        : '';

    const baseKey =
      LocalStorageKeyFactory.createDataGridSelectedPresetKey(instanceName);
    const key = LocalStorageKeyFactory.createUserScopedKey(userId, baseKey);
    LocalStorage.set(key, presetId);
  }

  // ===========================================================================
  // Old Service Worker Cleanup
  // ===========================================================================

  static wasOldServiceWorkerCleaned(): boolean {
    return LocalStorage.has(LocalStorageKeyFactory.OLD_SW_CLEANED_FLAG);
  }

  static markOldServiceWorkerCleaned(): void {
    LocalStorage.set(LocalStorageKeyFactory.OLD_SW_CLEANED_FLAG, 'true');
  }

  // ===========================================================================
  // Project Selection
  // ===========================================================================

  static wasProjectSelected(): boolean {
    return LocalStorage.get<string>(
      LocalStorageKeyFactory.SELECTION_HAPPENED_KEY,
      ''
    ) !== '';
  }

  static markProjectSelected(): void {
    LocalStorage.set(LocalStorageKeyFactory.SELECTION_HAPPENED_KEY, '1');
  }

  // ===========================================================================
  // Template Search
  // ===========================================================================

  static getTemplateSearchPhrase(): string {
    return LocalStorage.get<string>(
      LocalStorageKeyFactory.TEMPLATE_SEARCH_KEY,
      ''
    );
  }

  static setTemplateSearchPhrase(phrase: string): void {
    LocalStorage.set(LocalStorageKeyFactory.TEMPLATE_SEARCH_KEY, phrase);
  }

  // ===========================================================================
  // Viewed Template
  // ===========================================================================

  static getLastViewedTemplateId(projectId: string): string {
    const key = LocalStorageKeyFactory.createViewedTemplateKey(projectId);
    return LocalStorage.get<string>(key, '');
  }

  static setLastViewedTemplateId(projectId: string, templateId: string): void {
    const key = LocalStorageKeyFactory.createViewedTemplateKey(projectId);
    LocalStorage.set(key, templateId);
  }

  // ===========================================================================
  // Documentation Progress & Issues Visibility
  // ===========================================================================

  static getDocumentationProgressActive(projectId: string): boolean {
    const key = LocalStorageKeyFactory.createDocumentationProgressActiveKey(projectId);
    const savedState = LocalStorage.get<string>(key, '');
    return savedState ? Boolean(savedState) : true;
  }

  static setDocumentationProgressActive(projectId: string, active: boolean): void {
    const key = LocalStorageKeyFactory.createDocumentationProgressActiveKey(projectId);
    LocalStorage.set(key, active ? 'true' : '');
  }

  static getDocumentationIssuesVisible(projectId: string): boolean {
    const key = LocalStorageKeyFactory.createDocumentationIssuesVisibleKey(projectId);
    const savedState = LocalStorage.get<string>(key, '');
    return Boolean(savedState);
  }

  static setDocumentationIssuesVisible(projectId: string, visible: boolean): void {
    const key = LocalStorageKeyFactory.createDocumentationIssuesVisibleKey(projectId);
    LocalStorage.set(key, visible ? 'true' : '');
  }

  // ===========================================================================
  // Navigation Target
  // ===========================================================================

  static getNavigationTarget<T>(projectId: string): T | null {
    const key = LocalStorageKeyFactory.createProjectNavigationTargetKey(projectId);
    const result = LocalStorage.get<T>(key);
    return result.success ? result.value : null;
  }

  static setNavigationTarget<T>(projectId: string, target: T): void {
    const key = LocalStorageKeyFactory.createProjectNavigationTargetKey(projectId);
    LocalStorage.set(key, target);
  }

  // ===========================================================================
  // Cache
  // ===========================================================================

  static getCacheData<T>(id: string): T | null {
    const key = LocalStorageKeyFactory.createCacheDataKey(id);
    const result = LocalStorage.get<T>(key);
    return result.success ? result.value : null;
  }

  static setCacheData<T>(id: string, data: T, cacheId: string): void {
    const dataKey = LocalStorageKeyFactory.createCacheDataKey(id);
    const cacheIdKey = LocalStorageKeyFactory.createCacheIdKey(id);
    const lastUpdateKey = LocalStorageKeyFactory.createCacheLastUpdateKey(id);
    
    LocalStorage.set(dataKey, data);
    LocalStorage.set(cacheIdKey, cacheId);
    LocalStorage.set(lastUpdateKey, Date.now());
  }

  static getCacheId(id: string): string {
    const key = LocalStorageKeyFactory.createCacheIdKey(id);
    return LocalStorage.get<string>(key, '');
  }

  static getCacheLastUpdate(id: string): number {
    const key = LocalStorageKeyFactory.createCacheLastUpdateKey(id);
    return LocalStorage.get<number>(key, 0);
  }

  // ===========================================================================
  // Generic Persisted State (for usePersistedState hook)
  // ===========================================================================

  static getPersistedState<T>(key: string, defaultValue: T): T {
    const storageKey = LocalStorageKeyFactory.createGenericKey(key);
    return LocalStorage.get<T>(storageKey, defaultValue);
  }

  static setPersistedState<T>(key: string, value: T): void {
    const storageKey = LocalStorageKeyFactory.createGenericKey(key);
    LocalStorage.set(storageKey, value);
  }

  // ===========================================================================
  // Table Column Widths
  // ===========================================================================

  static getTableColumnWidths(tableKey: string): Record<string, number | undefined> {
    const storageKey = LocalStorageKeyFactory.createTableColumnWidthKey(tableKey);
    const savedData = LocalStorage.get<Record<string, number | undefined>>(storageKey, {});
    if (typeof savedData !== 'object' || savedData === null) {
      return {};
    }
    return savedData;
  }

  static setTableColumnWidth(tableKey: string, columnId: string | number, width: number): void {
    const state = this.getTableColumnWidths(tableKey);
    state[`${columnId}`] = width;
    const storageKey = LocalStorageKeyFactory.createTableColumnWidthKey(tableKey);
    LocalStorage.set(storageKey, state);
  }

  // ===========================================================================
  // Search Phrase
  // ===========================================================================

  static getSearchPhrase(projectId: string, entityLabel: string): string {
    const key = LocalStorageKeyFactory.createSearchPhraseKey(projectId, entityLabel);
    return LocalStorage.get<string>(key, '');
  }

  static setSearchPhrase(projectId: string, entityLabel: string, searchPhrase: string): void {
    const key = LocalStorageKeyFactory.createSearchPhraseKey(projectId, entityLabel);
    LocalStorage.set(key, searchPhrase);
  }

  // ===========================================================================
  // Chart Settings
  // ===========================================================================

  static getChartSetting(chartId: string, setting: string): string | null {
    const key = LocalStorageKeyFactory.createChartSettingKey(chartId, setting);
    const result = LocalStorage.get<string>(key);
    return result.success ? result.value : null;
  }

  static setChartSetting(chartId: string, setting: string, value: unknown): void {
    const key = LocalStorageKeyFactory.createChartSettingKey(chartId, setting);
    LocalStorage.set(key, value);
  }

  static getChartSettingObject<T>(chartId: string, setting: string): T | null {
    const key = LocalStorageKeyFactory.createChartSettingKey(chartId, setting);
    const result = LocalStorage.get<T>(key);
    return result.success ? result.value : null;
  }

  // ===========================================================================
  // PDF Report preferences
  // ===========================================================================

  static migrateLegacyReportLocalStorage(reportType: ReportType): void {
    migrateLegacyReportLocalStorage(reportType);
  }

  static getSavedReportConfig(reportType: ReportType): SavedReportConfig {
    return getSavedReportConfig(reportType);
  }

  static saveReportConfig(
    reportType: ReportType,
    partial: Partial<SavedReportConfig>
  ): void {
    saveReportConfig(reportType, partial);
  }

  static getReportFieldsContent(reportType: ReportType): ReportFieldsContent {
    return getSavedReportFieldsContent(reportType);
  }

  static setReportFieldsContent(
    reportType: ReportType,
    value: ReportFieldsContent
  ): void {
    saveFieldsContent(reportType, value);
  }

  static getReportSelectedFields(
    reportType: ReportType,
    intl: IntlShape,
    projectId: string,
    fieldsMap: HashMap<Field>,
    processId?: string
  ): LabelledEntity[] {
    return getSavedSelectedFields(
      reportType,
      intl,
      projectId,
      fieldsMap,
      processId
    );
  }

  static setReportSelectedFields(
    reportType: ReportType,
    projectId: string,
    value: string,
    processId?: string
  ): void {
    saveSelectedFields(reportType, projectId, value, processId);
  }

  static getReportAdditionalContentExpanded(
    reportType: ReportType,
    accordionId?: string,
    defaultExpanded = true
  ): boolean {
    return getReportAdditionalContentExpanded(
      reportType,
      accordionId,
      defaultExpanded
    );
  }

  static setReportAdditionalContentExpanded(
    reportType: ReportType,
    expanded: boolean,
    accordionId?: string
  ): void {
    saveReportAdditionalContentExpanded(reportType, expanded, accordionId);
  }

  static getSavedInspectionReportContentConfig(): SavedInspectionReportContentConfig {
    return getSavedInspectionReportContentConfig();
  }

  static saveInspectionReportContentConfig(
    config: SavedInspectionReportContentConfig
  ): void {
    saveInspectionReportContentConfig(config);
  }

  static getLastReportLocale(): LocaleCountryAndLanguageCode | null {
    return getLastReportLocale();
  }

  static saveLastReportLocale(localeTag: LocaleCountryAndLanguageCode): void {
    saveLastReportLocale(localeTag);
  }
}

